import React, { useState, useMemo, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { addDays, addWeeks, nextMonday, format, isSameDay, isSameWeek, startOfDay, isBefore } from 'date-fns';
import './customDatepicker.css';
import { parseTimeStringToHM } from 'utils/Helpers';
import arrowDown from 'assets/img/arrow-down.svg';
import TimeIcon from 'assets/img/time-date.svg';
import clearDate from 'assets/img/dateclosericon.svg';
import { Popover } from 'react-tiny-popover';
import Avatar from '../Avatar';
import { CLOCK_ICON } from '../../assets/image';

import addReminderIcon from 'assets/img/addtime-add-icon.svg';
import deleteReminderIcon from 'assets/img/addtime-deleted.svg';
import appToast, { TOAST_TYPE } from 'utils/AppToast';
import boardToast from '../../utils/boardToast';

const POPOVER_VIEWPORT_MARGIN = 16;

const keepPopoverInViewport = ({ popoverRect }) => {
  let top = 0;
  let left = 0;

  if (popoverRect.top < POPOVER_VIEWPORT_MARGIN) {
    top = POPOVER_VIEWPORT_MARGIN - popoverRect.top;
  } else if (popoverRect.bottom > window.innerHeight - POPOVER_VIEWPORT_MARGIN) {
    top = window.innerHeight - POPOVER_VIEWPORT_MARGIN - popoverRect.bottom;
  }

  if (popoverRect.left < POPOVER_VIEWPORT_MARGIN) {
    left = POPOVER_VIEWPORT_MARGIN - popoverRect.left;
  } else if (popoverRect.right > window.innerWidth - POPOVER_VIEWPORT_MARGIN) {
    left = window.innerWidth - POPOVER_VIEWPORT_MARGIN - popoverRect.right;
  }

  return { top, left };
};

export default function CustomDatepicker({
  defaultDate,
  handleDueDate,
  reminderOptionsData,
  milestone,
  showDateOnly = false,
  isNewMilestone = false,
  viewMode = false,
  isClosedStatus = false
}) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null); // normalized label like "2:15 pm"
  const [savedReminder, setSavedReminder] = useState(reminderOptionsData[0]?.value);
  const [reminder, setReminder] = useState(reminderOptionsData[0]?.value);
  const [savedDate, setSavedDate] = useState(null);
  const [savedTime, setSavedTime] = useState(null);
  const [tempTimeInput, setTempTimeInput] = useState(''); // for the editable input
  const [timeInputError, setTimeInputError] = useState(''); // validation message
  const [reminderOptions, setReminderOptions] = useState(reminderOptionsData);
  const [open, setOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [month, setMonth] = useState(new Date());
  const [reminderOpen, setReminderOpen] = useState(false);
  const [placementClass, setPlacementClass] = useState('place-top');
  const reminderDropdownRef = useRef(null);
  const reminderBtnRef = useRef(null);
  const popRef = useRef(null);
  const btnRef = useRef(null);
  const addTimeRef = useRef(null);
  const timeDropdownRef = useRef(null);

  const createReminderRow = (value = reminderOptionsData[0]?.value) => ({
    id: crypto.randomUUID(),
    reminderId: value,
    reminderSet: 0
  });

  const [reminders, setReminders] = useState([createReminderRow()]);
  const [savedReminders, setSavedReminders] = useState([createReminderRow()]);

  // ---------- Effects ----------

  const showNoneReminderToast = () => {
    boardToast('Please select a reminder before adding another one.', TOAST_TYPE.ERROR);
    // toast.warning('Please select a reminder before adding another one.');
  };

  const addReminder = () => {
    const lastReminder = reminders[reminders.length - 1];

    // 🚫 Block if last reminder is "None"
    if (!lastReminder || isNoneReminder(lastReminder.reminderId)) {
      showNoneReminderToast();
      return;
    }

    // ✅ Add new reminder at the BOTTOM
    setReminders((prev) => [
      ...prev,
      {
        ...createReminderRow(),
        reminderSet: prev.length
      }
    ]);
  };

  const deleteReminder = (id) => {
    setReminders((prev) => prev.filter((r) => r.id !== id).map((r, i) => ({ ...r, reminderSet: i })));
  };

  const updateReminderValue = (id, value) => {
    setReminders((prev) => prev.map((r) => (r.id === id ? { ...r, reminderId: value } : r)));
  };

  const resetDraftFromSaved = () => {
    setSelectedDate(savedDate);
    setSelectedTime(savedTime);
    setTempTimeInput(savedTime || '');
    setTimeInputError('');
    setReminder(savedReminder);
  };

  const isPastSelectedDate = useMemo(() => {
    if (!selectedDate) return false;
    return isBefore(startOfDay(selectedDate), startOfDay(new Date()));
  }, [selectedDate]);

  const mapBackendRemindersToUI = (backendReminders = []) => {
    if (!Array.isArray(backendReminders) || backendReminders.length === 0) {
      return [createReminderRow()];
    }

    return backendReminders.map((r, index) => ({
      id: r._id || crypto.randomUUID(),
      reminderId: r.kbDueDateReminderId?._id || reminderOptionsData[0]?.value,
      reminderSet: r.kbDueDateReminderSet
    }));
  };
  useEffect(() => {
    if (!open || !milestone) return;

    const backendReminders = milestone.kbDueDateReminders;

    const uiReminders = mapBackendRemindersToUI(backendReminders);

    setReminders(uiReminders);
    setSavedReminders(uiReminders);
  }, [milestone, open]);

  // initialize from defaultDate
  // initialize from defaultDate into both saved & draft
  useEffect(() => {
    if (defaultDate) {
      const parsed = new Date(defaultDate);
      if (!isNaN(parsed)) {
        const hrs = parsed.getHours();
        const mins = parsed.getMinutes();
        let timeLabel = null;
        if (hrs !== 0 || mins !== 0) {
          timeLabel = format(parsed, 'hh:mm a');
        }

        setSavedDate(parsed);
        setSavedTime(timeLabel);

        setSelectedDate(parsed);
        setSelectedTime(timeLabel);
        setTempTimeInput(timeLabel || '');
        setMonth(parsed);
      }
    } else {
      // no default -> clear saved & draft
      setSavedDate(null);
      setSavedTime(null);
      setSelectedDate(null);
      setSelectedTime(null);
      setTempTimeInput('');
    }
  }, [defaultDate]);

  useEffect(() => {
    if (!selectedTime) {
      const filteredoptions = reminderOptionsData.filter(
        (x) => !x.label.includes('minutes') && !x.label.includes('hour')
      );
      setReminderOptions(filteredoptions);
    } else {
      setReminderOptions(reminderOptionsData);
    }
  }, [selectedTime, reminderOptionsData]);

  // useEffect(() => {
  //   if (milestone) {
  //     const selectedReminder = reminderOptions.find((x) => x.value == milestone?.kbDueDateReminderId?._id)?.value;
  //     if (selectedReminder) {
  //       setReminder(selectedReminder);
  //     }
  //   }
  // }, [milestone, reminderOptions, open]);

  useEffect(() => {
    if (!reminderOpen) return;

    const handleClick = (e) => {
      const dropdownEl = reminderDropdownRef.current;
      const popoverEl = popRef.current;

      if (!popoverEl) return;

      // Click is inside the modal
      if (popoverEl.contains(e.target)) {
        // But NOT inside the reminder dropdown
        if (dropdownEl && !dropdownEl.contains(e.target)) {
          setReminderOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [reminderOpen]);

  const shortcuts = useMemo(() => {
    const today = new Date();
    const tomorrow = addDays(today, 1);
    const nextMon = nextMonday(today);

    return [
      { id: 'today', label: 'Today', date: today },
      { id: 'tomorrow', label: 'Tomorrow', date: tomorrow },
      { id: 'next-week', label: 'Next week', date: nextMon },
      { id: '1-week', label: '1 week', date: addWeeks(today, 1) },
      { id: '2-weeks', label: '2 weeks', date: addWeeks(today, 2) },
      { id: '4-weeks', label: '4 weeks', date: addWeeks(today, 4) }
    ];
  }, []);

  // Generate time slots every 15 minutes
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let h = 0; h < 24; h++) {
      for (let m of [0, 15, 30, 45]) {
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        const hh = hour12.toString().padStart(2, '0');
        const mm = m.toString().padStart(2, '0');
        const period = h < 12 ? 'am' : 'pm';
        slots.push(`${hh}:${mm} ${period}`);
      }
    }
    return slots;
  }, []);

  const toggleOpen = () => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        // opening -> start from saved values
        setMonth(savedDate || new Date());
        resetDraftFromSaved();
      } else {
        // closing without save via toggle -> discard edits
        resetDraftFromSaved();
        setTimeOpen(false);
        setReminderOpen(false);
      }
      return next;
    });
  };

  // choosing and clearing date/time
  const chooseDate = (date) => {
    setSelectedDate(date);
    setMonth(date);
  };

  const onCalendarSelect = (d) => {
    setSelectedDate(d);
    if (d) {
      setMonth(d);
    }
  };

  const goToday = () => {
    const d = new Date();
    setSelectedDate(d);
    setMonth(d);
  };

  const clearSelection = () => {
    setSelectedDate(null);
    setSelectedTime(null);
    setTempTimeInput('');
    setTimeInputError('');
    setTimeOpen(false);
    setReminderOpen(false);
    // no handleDueDate here – Save will send null if user wants to persist
  };

  // compute outsideLabel: show Today/Tomorrow/weekday (if in this week), otherwise show date
  const computeOutsideLabel = (selectedDate, selectedTime, showDateOnly) => {
    if (!selectedDate) {
      return showDateOnly ? '' : 'Select Date';
    }

    if (showDateOnly) {
      return format(selectedDate, 'MMM d'); // e.g. "Sep 29"
    }

    const today = new Date();
    const tomorrow = addDays(today, 1);

    let dayLabel;
    if (isSameDay(selectedDate, today)) {
      dayLabel = 'Today';
    } else if (isSameDay(selectedDate, tomorrow)) {
      dayLabel = 'Tomorrow';
    } else if (isSameWeek(selectedDate, today, { weekStartsOn: 1 })) {
      dayLabel = format(selectedDate, 'EEEE'); // full weekday name
    } else {
      dayLabel = format(selectedDate, 'MMM d, yyyy'); // fallback date format
    }

    return selectedTime ? `${dayLabel} · ${selectedTime}` : dayLabel;
  };

  const outsideLabel = computeOutsideLabel(selectedDate, selectedTime, showDateOnly);

  // ---- editable time handling with robust validation ----
  const confirmTempTimeInput = () => {
    setTimeInputError(''); // clear previous error
    const raw = tempTimeInput && tempTimeInput.trim();

    // empty -> clear time
    if (!raw) {
      setSelectedTime(null);
      setTempTimeInput('');
      setTimeOpen(false);
      return;
    }

    let hm = null;
    try {
      hm = parseTimeStringToHM(raw);
    } catch {
      hm = null;
    }

    if (!hm || !Number.isFinite(hm.h) || !Number.isFinite(hm.m)) {
      setTimeInputError("Invalid time format. Please use '12:00 PM'.");
      setTimeOpen(true);
      setTimeout(() => {
        const input = addTimeRef.current && addTimeRef.current.querySelector('input');
        if (input) input.focus();
      }, 0);
      return;
    }

    try {
      const tmp = new Date();
      tmp.setHours(hm.h, hm.m, 0, 0);

      if (isNaN(tmp.getTime())) throw new Error('Invalid date');

      const normalized = format(tmp, 'hh:mm a');
      setSelectedTime(normalized);
      setTempTimeInput(normalized);
      setTimeInputError('');
      setTimeOpen(false);
    } catch {
      setTimeInputError("Invalid time. Examples: '2:30 pm', '14:30', '2pm', '2:30'.");
      setTimeOpen(true);
      setTimeout(() => {
        const input = addTimeRef.current && addTimeRef.current.querySelector('input');
        if (input) input.focus();
      }, 0);
    }
  };

  const onSelectTime = (t) => {
    setSelectedTime(t);
    setTempTimeInput(t);
    setTimeInputError('');
    setTimeOpen(false);
  };

  const onTimeInputKeyDown = (e) => {
    if (timeInputError) setTimeInputError('');
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmTempTimeInput();
    } else if (e.key === 'Escape') {
      setTempTimeInput(selectedTime || '');
      setTimeInputError('');
      setTimeOpen(false);
    }
  };

  // focus time input when Add time is toggled
  useEffect(() => {
    if (timeOpen && addTimeRef.current) {
      const input = addTimeRef.current.querySelector('input');
      if (input) input.focus();
    }
  }, [timeOpen]);

  // ---- SAVE HANDLER (only place that calls handleDueDate) ----
  // CustomDatepicker.handleSave()
  // ---- helper to compare saved vs current due (date+time) ----
  const dueEqual = (savedDateVal, savedTimeVal, selDateVal, selTimeVal) => {
    // both null/undefined => equal
    if (!savedDateVal && !selDateVal) return true;
    // one present and the other not => not equal
    if (!!savedDateVal !== !!selDateVal) return false;

    // both present -> build comparable timestamps (minute precision)
    const buildTs = (d, timeLabel) => {
      const dt = new Date(d);
      dt.setSeconds(0, 0);
      if (timeLabel) {
        // parseTimeStringToHM returns {h,m} in your code
        const hm = parseTimeStringToHM(timeLabel);
        if (hm && Number.isFinite(hm.h) && Number.isFinite(hm.m)) {
          dt.setHours(hm.h, hm.m, 0, 0);
        } else {
          // fallback: interpret as midnight
          dt.setHours(0, 0, 0, 0);
        }
      } else {
        // no time -> midnight
        dt.setHours(0, 0, 0, 0);
      }
      return dt.getTime();
    };

    try {
      return buildTs(savedDateVal, savedTimeVal) === buildTs(selDateVal, selTimeVal);
    } catch {
      return false;
    }
  };

  // ---- UPDATED handleSave ----
  const handleSave = () => {
    // compute candidate dateToSend (same logic you had)
    let dateToSend = null;
    let hasTime = false;

    if (selectedDate) {
      const d = new Date(selectedDate);
      d.setSeconds(0, 0);

      const hm = parseTimeStringToHM(selectedTime);
      if (hm && Number.isFinite(hm.h) && Number.isFinite(hm.m)) {
        d.setHours(hm.h, hm.m, 0, 0);
        hasTime = true;
      } else {
        d.setHours(0, 0, 0, 0);
        hasTime = false;
      }

      dateToSend = d;
    }

    // determine hasReminder for the CURRENT selection (same rule as before)
    const curReminderOpt = reminderOptions.find((x) => x.value === reminder);
    const curIsNoneLabel = curReminderOpt?.label?.toLowerCase() === 'none';
    const curHasReminder = !!dateToSend && !curIsNoneLabel && reminder !== '690a5e9242606fb6991946b5';

    // determine hasReminder for the SAVED state (so removing/adding reminder is detected)
    const savedReminderOpt = reminderOptions.find((x) => x.value === savedReminder);
    const savedIsNoneLabel = savedReminderOpt?.label?.toLowerCase() === 'none';
    const savedHasReminder = !!savedDate && !savedIsNoneLabel && savedReminder !== '690a5e9242606fb6991946b5';

    // If nothing changed — date/time equal AND reminder equal — do NOT call handleDueDate
    const sameDue = dueEqual(savedDate, savedTime, selectedDate, selectedTime);
    const sameReminder = reminder === savedReminder;
    // also handle case where both had no reminder presence but reminder value differs only by the special 'none' label logic
    const sameHasReminder = !!curHasReminder === !!savedHasReminder && sameReminder;

    // if (sameDue && sameHasReminder) {
    //   // nothing to do — just close UI like before
    //   setOpen(false);
    //   setTimeOpen(false);
    //   setReminderOpen(false);
    //   return;
    // }
    const kbDueDateReminders = reminders.map((r, index) => ({
      kbDueDateReminderId: r.reminderId,
      kbDueDateReminderSet: !(r.reminderId == '690a5e9242606fb6991946b5')
    }));
    // otherwise, proceed to call handleDueDate (only if selectedDate exists)
    if (dateToSend) {
      handleDueDate(dateToSend, kbDueDateReminders, hasTime);

      // update local saved values so future saves compare correctly
      setSavedDate(dateToSend);
      setSavedTime(selectedTime);
      setSavedReminder(reminder);
    } else {
      // If user cleared the date (dateToSend === null) this is a change compared to savedDate (if savedDate existed)
      // Call handleDueDate with null date to persist the clearing action if it differs
      // Only call when savedDate !== null (i.e. there was a previously saved date)
      if (savedDate) {
        // handleDueDate(null, reminder, false, false);
        handleDueDate(null, kbDueDateReminders, false);
        setSavedDate(null);
        setSavedTime(null);
        setSavedReminder(reminderOptionsData[0]?.value); // or keep current reminder if you prefer
      }
    }

    setOpen(false);
    setTimeOpen(false);
    setReminderOpen(false);
  };

  const isNoneReminder = (reminderId) => {
    const opt = reminderOptionsData.find((x) => x.value === reminderId);
    return opt?.label?.toLowerCase() === 'none' || reminderId === '690a5e9242606fb6991946b5';
  };

  const getUsedReminderValues = (excludeId) => {
    return reminders
      .filter((r) => r.id !== excludeId)
      .map((r) => r.reminderId)
      .filter(Boolean);
  };
  const isOptionDisabled = (optionValue, currentRowId) => {
    // Allow "None" to always be selectable
    if (isNoneReminder(optionValue)) return false;

    const usedValues = getUsedReminderValues(currentRowId);
    return usedValues.includes(optionValue);
  };

  // ----------------- JSX -----------------
  const popoverContent = (
    <div
      className={`cd-popover no-close-area ${placementClass}`}
      ref={popRef}
      // onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onFocus={(e) => e.stopPropagation()}>
      {/* Header input inside popover */}
      <div className={`cd-header-row ${timeInputError ? 'error' : ''}`}>
        <div className="cd-header-input cd-header-input2">
          <svg className="cd-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"
            />
          </svg>
          <span className="cd-input">{outsideLabel}</span>
          {selectedDate && (
            <img
              src={clearDate}
              onClick={() => {
                clearSelection();
              }}
              alt=""
              style={{ cursor: 'pointer' }}
            />
          )}
        </div>

        {/* Add time input + button: visible only when a date is selected */}
        <div className="cd-header-right cd-header-right2">
          {selectedDate && (
            <div className="cd-addtime-wrap" ref={addTimeRef}>
              {/* Editable input that also acts as dropdown toggle */}
              <div className="cd-addtime-input-wrap cd-addtime-input-wrap2" onMouseDown={(e) => e.stopPropagation()}>
                <input
                  onMouseDown={(e) => e.stopPropagation()}
                  type="text"
                  className={`cd-addtime-input ${timeInputError ? 'error' : ''}`}
                  placeholder="Add time"
                  value={tempTimeInput}
                  onFocus={() => console.log('INPUT FOCUS')}
                  onBlur={() => confirmTempTimeInput()}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTempTimeInput(value);
                    setSelectedTime(value); // 👈 commit immediately
                    if (timeInputError) setTimeInputError('');
                  }}
                  // onFocus={() => {
                  //   setTimeOpen(true);
                  //   setTimeInputError('');
                  // }}
                  onKeyDown={onTimeInputKeyDown}
                  aria-haspopup="listbox"
                  aria-expanded={timeOpen}
                  aria-label="Add time"
                />

                <button
                  type="button"
                  className="cd-addtime-btn-icon"
                  onClick={() => setTimeOpen((v) => !v)}
                  aria-expanded={timeOpen}
                  aria-label="Toggle time list">
                  <img src={arrowDown} alt="" />
                </button>
              </div>

              {timeOpen && (
                <div className="cd-time-dropdown" ref={timeDropdownRef} role="listbox" aria-label="Time options">
                  <div className="cd-time-list">
                    <button
                      type="button"
                      className="cd-time-item cd-clear-time"
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={() => {
                        setSelectedTime(null);
                        setTempTimeInput('');
                        setTimeInputError('');
                        setTimeOpen(false);
                      }}
                      aria-label="Remove selected time">
                      Clear
                    </button>
                    {timeSlots.map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`cd-time-item ${
                          selectedTime && t.toLowerCase() === selectedTime.toLowerCase() ? 'active' : ''
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                        }}
                        onClick={() => onSelectTime(t)}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {timeInputError && (
          <div className="cd-time-error" role="alert" aria-live="polite">
            {timeInputError}
          </div>
        )}
      </div>

      <div className="cd-pop-inner">
        {/* LEFT: shortcuts + reminder */}
        <div className="cd-left">
          <div className="cd-shortcuts">
            {shortcuts.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => chooseDate(s.date)}
                className={`cd-shortcut ${selectedDate && isSameDay(selectedDate, s.date) ? 'active' : ''}`}>
                <div className="cd-shortcut-main">
                  <div className="cd-short-label">{s.label}</div>
                  <div className="cd-short-day">{format(s.date, 'EEE')}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="cd-reminder">
            <label className="cd-reminder-label">Set Reminder</label>

            {reminders.map((r, index) => (
              <div key={r.id} className="cd-addtime-wrap-cover">
                <div className="cd-addtime-wrap">
                  <div className="cd-addtime-input-wrap cd-addtime-input-wrap2">
                    <div
                      className="cd-addtime-input cd-addtime-input--readonly"
                      onClick={() => {
                        if (selectedDate && !isPastSelectedDate) {
                          setReminderOpen(r.id);
                        }
                      }}>
                      {reminderOptions.find((x) => x.value === r.reminderId)?.label || 'None'}
                    </div>

                    <button type="button" className="cd-addtime-btn-icon" onClick={() => setReminderOpen(r.id)}>
                      <img src={arrowDown} alt="" />
                    </button>
                  </div>

                  {reminderOpen === r.id && (
                    <div ref={reminderDropdownRef} className="cd-time-dropdown cd-time-dropdown--top">
                      <div className="cd-time-list">
                        {reminderOptions.map((opt) => {
                          const disabled = isOptionDisabled(opt.value, r.id);

                          return (
                            <button
                              key={opt.value}
                              disabled={disabled}
                              className={`cd-time-item
        ${opt.value === r.reminderId ? 'active' : ''}
        ${disabled ? 'disabled' : ''}`}
                              onClick={() => {
                                if (disabled) return;
                                updateReminderValue(r.id, opt.value);
                                setReminderOpen(false);
                              }}>
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Icons */}
                {/* <div className="addtimeaddIcon"> */}
                {index === reminders.length - 1 && (
                  <div className="addtimeaddIcon" onClick={addReminder}>
                    {' '}
                    <img src={addReminderIcon} alt="add" />{' '}
                  </div>
                )}

                {index !== reminders.length - 1 && (
                  <div className="addtimeaddIcon" onClick={() => deleteReminder(r.id)}>
                    {' '}
                    <img src={deleteReminderIcon} alt="delete" />
                  </div>
                )}
                {/* </div> */}
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: calendar with custom navbar */}
        <div className="cd-right">
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={onCalendarSelect}
            month={month}
            onMonthChange={setMonth}
            disabled={{ before: new Date() }}
            navbarElement={({ displayMonth, onPrevClick, onNextClick }) => (
              <div className="cd-calendar-header">
                <div className="cd-month-title">{format(displayMonth, 'LLLL yyyy')}</div>
                <div className="cd-calendar-controls">
                  <button type="button" className="cd-small-btn" onClick={goToday}>
                    Today
                  </button>
                  <button type="button" className="cd-icon-small" onClick={onPrevClick}>
                    ▲
                  </button>
                  <button type="button" className="cd-icon-small" onClick={onNextClick}>
                    ▼
                  </button>
                </div>
              </div>
            )}
          />
        </div>
      </div>

      {/* FOOTER: Save button */}
      <div className="cd-footer">
        <button
          type="button"
          // className={`cd-footer-btn cd-footer-btn-primary ${!selectedDate ? 'disabledSave' : ''}`}
          className={`cd-footer-btn cd-footer-btn-primary`}
          onClick={handleSave}>
          Save
        </button>
      </div>
    </div>
  );

  return (
    <div className="cd-root">
      <Popover
        isOpen={open}
        positions={showDateOnly ? ['top', 'right', 'left'] : ['bottom', 'top', 'right', 'left']}
        padding={8}
        parentElement={document.body}
        reposition={false}
        boundaryInset={POPOVER_VIEWPORT_MARGIN}
        transform={keepPopoverInViewport}
        transformMode="relative"
        captureFocus={false}
        clickOutsideCapture={false}
        onBlur={() => console.log('INPUT BLURRED')}
        containerClassName="no-close-area"
        onClickOutside={(e) => {
          if (addTimeRef.current?.contains(e.target)) return;
          if (timeDropdownRef.current?.contains(e.target)) return;
          if (reminderDropdownRef.current?.contains(e.target)) return;

          setOpen(false);
          setTimeOpen(false);
          setReminderOpen(false);
          if (isNewMilestone) {
            resetDraftFromSaved();
          }
        }}
        content={popoverContent}>
        {/* Trigger stays the same, but no popover DOM below it anymore */}
        <button
          disabled={viewMode || isClosedStatus}
          // tabIndex={-1}
          type="button"
          className="cd-icon-btn"
          onClick={toggleOpen}
          ref={btnRef}
          aria-expanded={open}
          aria-haspopup="dialog"
          title="Select due date">
          {isNewMilestone ? (
            <>
              <img src={TimeIcon} alt="TimeIcon-icon" className="TimeIcon" />
              <div className="task-datepicker">{outsideLabel ? outsideLabel : 'Select Date'}</div>
            </>
          ) : showDateOnly ? (
            <div className={`d-flex align-items-center react-datepicker-wrapper ${!defaultDate ? "gap-2" : ""}`}>
              <div className="hover_controls">
                {!defaultDate && (
                  <Avatar
                    size={"extraSmall"}
                    style={{ border: 1, borderStyle: 'dashed', borderColor: '#CBD5E1' }}>
                    <img src={CLOCK_ICON} alt="plus-button-icon" />
                  </Avatar>
                )}
              </div>
              <div className="task-datepicker">{outsideLabel ? outsideLabel : ''}</div>
            </div>
          ) : (
            <>
              <span className={`cd-toggle-text ${selectedDate ? 'has-value' : ''}`}>{outsideLabel}</span>
              <svg
                className={`cd-dropdown-arrow ${open ? 'open' : ''}`}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </>
          )}
        </button>
      </Popover>
    </div>
  );
}
